/**
 * Community Fit Section JavaScript
 * Handles Masonry layout, popup modals, video controls, image sliders, and wishlist functionality
 */

class CommunityFit {
  constructor() {
    // Singleton pattern - prevent multiple instances
    if (CommunityFit.instance) {
      // console.log('CommunityFit: Returning existing instance');
      return CommunityFit.instance;
    }

    this.currentPopup = null;
    this.currentSlider = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.masonry = null;
    this.currentColumns = 4; // Default column count
    this.resizeListenerAdded = false;
    this.scrollPosition = 0;
    this._layoutRaf = null;

    CommunityFit.instance = this;

    this.init();
  }

    init() {
    // console.log('CommunityFit: Initializing...');

    // Wait for Masonry library to load
    if (typeof Masonry === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    this.initMasonry();
    this.bindEvents();
    this.initializeVideos();
    this.initDragScrolling();
    this.checkLazyLoading();
    this.setupIOSAutoplayWorkarounds();
    // console.log('CommunityFit: Initialized successfully');
  }

  initMasonry() {
    const grid = document.querySelector('.community-fit-grid');
    if (!grid) {
      // console.error('CommunityFit: Grid element not found');
      return;
    }

    const initMasonryLayout = () => {
      // Get current viewport width for responsive configuration
      const screenWidth = window.innerWidth;
      let columns = 4;

      if (screenWidth >= 1440) {
        columns = 6;
      } else if (screenWidth <= 1200 && screenWidth > 768) {
        columns = 3;
      } else if (screenWidth <= 768) {
        columns = 2;
      }

      // Get gutter values from CSS custom properties
      const computedStyle = getComputedStyle(grid);
      const horizontalGutter = parseInt(computedStyle.getPropertyValue('--horizontal-gutter')) || 10;
      const verticalGutter = parseInt(computedStyle.getPropertyValue('--vertical-gutter')) || 16;

      // console.log('CommunityFit: Gutter values - Horizontal:', horizontalGutter, 'Vertical:', verticalGutter);

      // Calculate column width and set it on all items
      const gridWidth = grid.offsetWidth;
      const itemWidth = (gridWidth - (horizontalGutter * (columns - 1))) / columns;

      // Set width on all items before initializing Masonry
      const items = grid.querySelectorAll('.community-fit-item');
      items.forEach(item => {
        item.style.width = `${itemWidth}px`;
      });

      this.masonry = new Masonry(grid, {
        itemSelector: '.community-fit-item',
        columnWidth: itemWidth,
        gutter: horizontalGutter, // Horizontal spacing between columns
        horizontalOrder: true, // This ensures left-to-right filling!
        resize: true
      });

      // Mark grid as initialized
      grid.classList.add('masonry-initialized');

      // Layout all items
      this.masonry.layout();

      // Update the current columns tracker
      this.currentColumns = columns;

      // console.log('CommunityFit: Masonry initialized with', columns, 'columns, itemWidth:', itemWidth);
      // console.log('CommunityFit: Horizontal gutter:', horizontalGutter, 'Vertical gutter:', verticalGutter);
    };

    initMasonryLayout();

    const scheduleLayout = () => {
      if (!this.masonry || this._layoutRaf != null) return;
      this._layoutRaf = requestAnimationFrame(() => {
        this._layoutRaf = null;
        if (this.masonry) this.masonry.layout();
      });
    };

    const images = grid.querySelectorAll('img');
    let loadedImages = 0;
    const totalImages = images.length;

    if (totalImages > 0) {
      images.forEach(img => {
        const onImageLoad = () => {
          loadedImages++;
          scheduleLayout();
        };

        if (img.complete) {
          loadedImages++;
        } else {
          img.addEventListener('load', onImageLoad);
          img.addEventListener('error', onImageLoad);
        }
      });

      if (loadedImages === totalImages) {
        scheduleLayout();
      }
    }

                // Simple, stable resize handler - never destroy Masonry instance
    if (!this.resizeListenerAdded) {
      this.resizeListenerAdded = true;
      let resizeTimeout;

      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          this.handleResize();
        }, 100); // Shorter timeout, but simpler logic
      });
    }
  }

  handleResize() {
    const grid = document.querySelector('.community-fit-grid');
    if (!this.masonry || !grid || grid.offsetWidth === 0) {
      return;
    }

    try {
      // Get current screen width and determine columns
      const screenWidth = window.innerWidth;
      let newColumns = 4;

      if (screenWidth >= 1440) {
        newColumns = 6;
      } else if (screenWidth <= 1200 && screenWidth > 768) {
        newColumns = 3;
      } else if (screenWidth <= 768) {
        newColumns = 2;
      }

      // Get gutter from CSS
      const computedStyle = getComputedStyle(grid);
      const horizontalGutter = parseInt(computedStyle.getPropertyValue('--horizontal-gutter')) || 10;

      // Calculate new item width
      const gridWidth = grid.offsetWidth;
      const itemWidth = (gridWidth - (horizontalGutter * (newColumns - 1))) / newColumns;

      // Update all item widths
      const items = grid.querySelectorAll('.community-fit-item');
      items.forEach(item => {
        item.style.width = `${itemWidth}px`;
      });

      // Update Masonry configuration
      this.masonry.options.columnWidth = itemWidth;
      this.masonry.options.gutter = horizontalGutter;

      // Reload items and layout - this is more stable than destroy/recreate
      this.masonry.reloadItems();
      this.masonry.layout();

      // Update column tracking
      this.currentColumns = newColumns;

      // console.log('CommunityFit: Resized to', newColumns, 'columns, itemWidth:', itemWidth);

    } catch (error) {
      // console.error('CommunityFit: Resize error:', error);
      // Don't attempt recovery, just log the error
    }
  }

  initializeVideos() {
    // Initialize all grid videos
    const videos = document.querySelectorAll('.community-fit-video');
    // console.log('CommunityFit: Found videos:', videos.length);

    videos.forEach(video => {
      const videoId = video.getAttribute('data-video-id');
      // console.log('CommunityFit: Initializing video:', videoId);

      // Set initial control state
      this.updateVideoControl(videoId, video.paused);

      // Set up dynamic aspect ratio handling
      this.setupVideoAspectRatio(video);

      // Video event listeners
      video.addEventListener('play', () => {
        this.updateVideoControl(videoId, false);
      });

      video.addEventListener('pause', () => {
        this.updateVideoControl(videoId, true);
      });

      // iOS autoplay handling - try to play when video is ready
      video.addEventListener('loadedmetadata', () => {
        this.attemptAutoplay(video, videoId);
      });

      video.addEventListener('canplay', () => {
        this.attemptAutoplay(video, videoId);
      });

      // Enhanced intersection observer for autoplay and pause
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Video is visible - try to autoplay
            this.attemptAutoplay(video, videoId);
          } else {
            // Video not visible - pause
            if (!video.paused) {
              video.pause();
              this.updateVideoControl(videoId, true);
            }
          }
        });
      }, { threshold: 0.3 });

      observer.observe(video);

      // Store observer for cleanup
      video._intersectionObserver = observer;
    });
  }

    attemptAutoplay(video, videoId) {
    // Skip if video is already playing or if it's currently attempting to play
    if (!video.paused || video._isPlaying) {
      return;
    }

    // Mark as attempting to play to prevent multiple simultaneous attempts
    video._isPlaying = true;

    // Check if video is ready to play
    if (video.readyState < 2) {
      video._isPlaying = false; // Clear flag since we're not attempting yet

      // Try again when ready
      video.addEventListener('loadeddata', () => {
        this.attemptAutoplay(video, videoId);
      }, { once: true });

      // Fallback timeout to prevent getting stuck
      setTimeout(() => {
        video._isPlaying = false;
      }, 5000);

      return;
    }

    // Ensure video has the correct attributes for iOS
    video.muted = true;
    video.playsInline = true;
    video.controls = false;

    // Set volume to 0 as extra precaution
    video.volume = 0;

    // Load the video if not loaded
    if (video.readyState === 0) {
      video.load();
    }

    // Attempt to play
    const playPromise = video.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          video._isPlaying = false;
          this.updateVideoControl(videoId, false);
          video._autoplaySucceeded = true;
        })
        .catch(error => {
          video._isPlaying = false;

          // Autoplay was prevented (common on iOS)
          this.updateVideoControl(videoId, true);

          // Add click handler to allow manual play
          this.addManualPlayHandler(video, videoId);

          // For iOS, try again after a short delay (sometimes works)
          if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !video._retryAttempted) {
            video._retryAttempted = true;
            setTimeout(() => {
              if (!video.paused) return; // Don't retry if already playing

              const retryPromise = video.play();
              if (retryPromise) {
                retryPromise.catch(() => {
                  // Ignore retry failures
                });
              }
            }, 500);
          }
        });
    } else {
      // If play() doesn't return a promise, clear the flag
      video._isPlaying = false;
    }
  }

  addManualPlayHandler(video, videoId) {
    // Check if handler already exists
    if (video._manualPlayHandler) {
      return;
    }

        const playHandler = () => {
      video.play()
        .then(() => {
          this.updateVideoControl(videoId, false);
          // Remove the manual play handler after successful play
          video.removeEventListener('click', playHandler);
          video._manualPlayHandler = null;
        })
        .catch(error => {
          // Manual play failed - keep handler for retry
        });
    };

    video.addEventListener('click', playHandler);
    video._manualPlayHandler = playHandler;
  }

  setupVideoAspectRatio(video) {
    const wrapper = video.closest('.community-fit-video-wrapper');
    if (!wrapper) return;

    const updateAspectRatio = () => {
      if (video.videoWidth && video.videoHeight) {
        // Calculate the actual aspect ratio (height / width * 100)
        const aspectRatio = video.videoHeight / video.videoWidth;
        const paddingBottom = aspectRatio * 100;

        // Set the padding-bottom inline to match the video's aspect ratio
        wrapper.style.paddingBottom = `${paddingBottom}%`;

        // console.log('CommunityFit: Video dimensions:', video.videoWidth, 'x', video.videoHeight);
        // console.log('CommunityFit: Set padding-bottom:', `${paddingBottom}%`);

        // Re-layout Masonry after aspect ratio is updated
        if (this.masonry) {
          setTimeout(() => {
            this.masonry.layout();
          }, 100);
        }
      } else {
        // console.log('CommunityFit: Video dimensions not available yet');
      }
    };

    // Try to update immediately if metadata is already loaded
    if (video.readyState >= 1) {
      updateAspectRatio();
    }

    // Listen for when video metadata loads
    video.addEventListener('loadedmetadata', updateAspectRatio);

    // Also listen for when video can play (as a fallback)
    video.addEventListener('canplay', updateAspectRatio);

    // Fallback: Try to update after a delay
    setTimeout(() => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        updateAspectRatio();
      }
    }, 1000);
  }

  bindEvents() {
    // Open popup events
    document.addEventListener('click', (e) => {
      const openButton = e.target.closest('[data-open-popup]');
      if (openButton) {
        e.preventDefault();
        const popupId = openButton.getAttribute('data-open-popup');
        this.openPopup(popupId);
      }
    });

    // Close popup events
    document.addEventListener('click', (e) => {
      const closeButton = e.target.closest('[data-close-popup]');
      if (closeButton) {
        e.preventDefault();
        this.closePopup();
      }

      // Close on overlay click
      if (e.target.classList.contains('community-fit-popup-overlay')) {
        this.closePopup();
      }
    });

    // Video control events
    document.addEventListener('click', (e) => {
      const videoControl = e.target.closest('[data-video-control]');
      if (videoControl) {
        e.preventDefault();
        e.stopPropagation();
        const videoId = videoControl.getAttribute('data-video-control');
        this.toggleVideo(videoId);
      }
    });

    // Direct video click events
    document.addEventListener('click', (e) => {
      const video = e.target.closest('.community-fit-video');
      if (video) {
        e.preventDefault();
        e.stopPropagation();
        const videoId = video.getAttribute('data-video-id');
        this.toggleVideo(videoId);
      }
    });

    // Slider navigation
    document.addEventListener('click', (e) => {
      const dot = e.target.closest('.community-fit-popup-dot');
      if (dot) {
        e.preventDefault();
        const slideIndex = parseInt(dot.getAttribute('data-slide-to'));
        const slider = dot.closest('.community-fit-popup-slider');
        if (slider) {
          const sliderId = slider.getAttribute('data-slider');
                    // Use the same dual-path approach as drag functionality
          if (sliderId) {
            this.goToSlide(sliderId, slideIndex);
          } else {
            this.directSlideChange(slider, slideIndex);
          }
        }
      }
    });

    // Wishlist events
    document.addEventListener('click', (e) => {
      const wishlistButton = e.target.closest('.community-fit-popup-wishlist');
      if (wishlistButton) {
        e.preventDefault();
        e.stopPropagation();
        const productId = wishlistButton.getAttribute('data-product-id');
        this.toggleWishlist(productId, wishlistButton);
      }
    });

    // Product link events for Stikky integration
    document.addEventListener('click', (e) => {
      const productLink = e.target.closest('.community-fit-popup-product-link[data-product-id]');
      if (productLink && navigator.userAgent.includes('source:stikky')) {
        e.preventDefault();
        e.stopPropagation();
        const productId = productLink.getAttribute('data-product-id');
        this.openProductWithStikky(productId);
      }
    });

    // Collection link events for Stikky integration
    document.addEventListener('click', (e) => {
      const collectionLink = e.target.closest('.community-fit-popup-collection a[data-collection-id]');
      const alternativeLink = e.target.closest('.community-fit-popup-collection a[data-alternative-url]');
      if (navigator.userAgent.includes('source:stikky')) {
        e.preventDefault();
        e.stopPropagation();
        if (collectionLink) {
          const collectionId = collectionLink.getAttribute('data-collection-id');
          this.openCollectionWithStikky(collectionId);
        } else if (alternativeLink) {
          const url = alternativeLink.getAttribute('data-alternative-url');
          this.openUrlWithStikky(url);
        }
      }
    });

    // Social link events for Stikky integration
    document.addEventListener('click', (e) => {
      const socialLink = e.target.closest('.community-fit-popup-social-link, .community-fit-social-overlay');
      if (socialLink && navigator.userAgent.includes('source:stikky')) {
        e.preventDefault();
        e.stopPropagation();
        const url = socialLink.href || socialLink.getAttribute('href');
        if (url) {
          this.openUrlWithStikky(url);
        }
      }
    });

    // Keyboard events
    document.addEventListener('keydown', (e) => {
      if (this.currentPopup) {
        if (e.key === 'Escape') {
          this.closePopup();
        } else if (e.key === 'ArrowLeft' && this.currentSlider) {
          e.preventDefault();
          this.previousSlide();
        } else if (e.key === 'ArrowRight' && this.currentSlider) {
          e.preventDefault();
          this.nextSlide();
        }
      }
    });

    // Touch events for mobile swiping - DISABLED
    // (Now handled by setupDragSliding with better local event listeners)
    /*
    document.addEventListener('touchstart', (e) => {
      if (this.currentSlider && e.target.closest('.community-fit-popup-slides')) {
        this.touchStartX = e.changedTouches[0].screenX;
      }
    });

    document.addEventListener('touchend', (e) => {
      if (this.currentSlider && e.target.closest('.community-fit-popup-slides')) {
        this.touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe();
      }
    });
    */
  }

      openPopup(popupId) {
    if (this.currentPopup) return;

    const container = document.getElementById('community-fit-popup-container');
    const template = document.querySelector(`[data-popup-template="${popupId}"]`);

    if (!template || !container) {
      const fallback = document.getElementById(`popup-${popupId}`);
      if (fallback) {
        this._openPopupElement(fallback);
      }
      return;
    }

    container.innerHTML = '';
    const clone = template.content.cloneNode(true);
    container.appendChild(clone);

    const popup = container.querySelector(`[data-popup-id="${popupId}"]`);
    if (!popup) return;

    this._openPopupElement(popup);
  }

  _openPopupElement(popup) {
    this.currentPopup = popup;
    popup.classList.add('active');

    setTimeout(() => {
      if (window.innerWidth > 768) {
        popup.classList.add('desktop-slide');
      }
    }, 50);

    this.disableScroll();

    const slider = popup.querySelector('.community-fit-popup-slider');
    if (slider) {
      this.currentSlider = slider;
      this.initializeSlider(slider);
    }

    const productSliders = popup.querySelectorAll('.community-fit-popup-products-list.slider');
    productSliders.forEach(productSlider => {
      this.setupDragScrolling(productSlider);
    });

    this.initializePopupVideos(popup);
    this.initializeNotifyButton(popup);
    this.pauseAllGridVideos();
  }

      closePopup() {
    if (!this.currentPopup) return;

    // console.log('CommunityFit: Closing popup');

    // Cleanup drag event listeners for current slider
    if (this.currentSlider) {
      const slidesContainer = this.currentSlider.querySelector('.community-fit-popup-slides');
      if (slidesContainer && slidesContainer._dragHandlers) {
        const handlers = slidesContainer._dragHandlers;

                      // Remove global event listeners
      document.removeEventListener('mouseup', handlers.mouseUpHandler);
      document.removeEventListener('mousemove', handlers.mouseMoveHandler);

      // Remove local event listeners
      slidesContainer.removeEventListener('mousedown', handlers.mouseDownHandler);
      slidesContainer.removeEventListener('click', handlers.clickHandler);

      // Remove touch event listeners
      if (handlers.touchStartHandler) {
        slidesContainer.removeEventListener('touchstart', handlers.touchStartHandler);
        slidesContainer.removeEventListener('touchend', handlers.touchEndHandler);
        slidesContainer.removeEventListener('touchmove', handlers.touchMoveHandler);
      }

        // Remove touch event listeners
        if (handlers.touchStartHandler) {
          slidesContainer.removeEventListener('touchstart', handlers.touchStartHandler);
          slidesContainer.removeEventListener('touchend', handlers.touchEndHandler);
          slidesContainer.removeEventListener('touchmove', handlers.touchMoveHandler);
        }

        // Clear stored handlers
        delete slidesContainer._dragHandlers;

        // console.log('CommunityFit: Cleaned up drag event listeners');
      }
    }

    // Cleanup product slider drag handlers
    if (this.currentPopup) {
      const productSliders = this.currentPopup.querySelectorAll('.community-fit-popup-products-list.slider');
      productSliders.forEach(slider => {
        if (slider._productScrollHandlers) {
          const handlers = slider._productScrollHandlers;

          // Remove event listeners
          slider.removeEventListener('mousedown', handlers.mouseDownHandler);
          slider.removeEventListener('mouseleave', handlers.mouseUpHandler);
          slider.removeEventListener('mouseup', handlers.mouseUpHandler);
          slider.removeEventListener('mousemove', handlers.mouseMoveHandler);
          slider.removeEventListener('click', handlers.clickHandler, true);

          // Remove child element listeners
          if (handlers.clickableElements) {
            handlers.clickableElements.forEach(element => {
              element.removeEventListener('click', handlers.preventChildClicks, true);
            });
          }

          // Clear stored handlers
          delete slider._productScrollHandlers;
        }
      });
    }

    // Cleanup notify button listeners/timers
    if (this.currentPopup._notifyCleanup) {
      this.currentPopup._notifyCleanup();
      delete this.currentPopup._notifyCleanup;
    }

    this.currentPopup.classList.remove('active');

    // Remove animation classes
    this.currentPopup.classList.remove('desktop-slide');

    // Re-enable page scroll
    this.enableScroll();

    // Pause popup videos and reset progress
    const popupVideos = this.currentPopup.querySelectorAll('.community-fit-popup-video');
    popupVideos.forEach(video => {
      video.pause();
      video.currentTime = 0;

      // Reset progress bar
      const videoId = video.getAttribute('data-popup-video');
      const progressBar = this.currentPopup.querySelector(`[data-progress="${videoId}"]`);
      if (progressBar) {
        progressBar.style.width = '0%';
      }
    });

    const container = document.getElementById('community-fit-popup-container');
    if (container && container.contains(this.currentPopup)) {
      this.currentPopup.remove();
    }

    this.currentPopup = null;
    this.currentSlider = null;
  }

  toggleVideo(videoId) {
    // console.log('CommunityFit: Toggling video:', videoId);

    const video = document.querySelector(`[data-video-id="${videoId}"]`);

    if (!video) {
      // console.error('CommunityFit: Video not found:', videoId);
      return;
    }

    if (video.paused) {
      video.play().then(() => {
        // console.log('CommunityFit: Video playing:', videoId);
        this.updateVideoControl(videoId, false);
      }).catch(err => {
        // console.error('CommunityFit: Error playing video:', err);
      });
    } else {
      video.pause();
      // console.log('CommunityFit: Video paused:', videoId);
      this.updateVideoControl(videoId, true);
    }
  }

  updateVideoControl(videoId, isPaused) {
    const control = document.querySelector(`[data-video-control="${videoId}"]`);
    if (!control) {
      // console.log('CommunityFit: No control found for video:', videoId);
      return;
    }

    const playIcon = control.querySelector('.play-icon');
    const pauseIcon = control.querySelector('.pause-icon');

    if (isPaused) {
      playIcon?.classList.remove('hidden');
      pauseIcon?.classList.add('hidden');
      control.setAttribute('aria-label', 'Play video');
    } else {
      playIcon?.classList.add('hidden');
      pauseIcon?.classList.remove('hidden');
      control.setAttribute('aria-label', 'Pause video');
    }

    // console.log('CommunityFit: Updated control for video:', videoId, 'paused:', isPaused);
  }

  pauseAllGridVideos() {
    const gridVideos = document.querySelectorAll('.community-fit-video');
    gridVideos.forEach(video => {
      if (!video.paused) {
        video.pause();
        const videoId = video.getAttribute('data-video-id');
        this.updateVideoControl(videoId, true);
      }
    });
  }

  initializeSlider(slider) {
    const sliderId = slider.getAttribute('data-slider');
    // console.log('CommunityFit: Initializing slider:', sliderId);

    this.goToSlide(sliderId, 0);

    // Setup drag sliding for desktop
    this.setupDragSliding(slider);
  }

  goToSlide(sliderId, slideIndex) {
    // console.log('CommunityFit: goToSlide called with sliderId:', sliderId, 'slideIndex:', slideIndex);
    const slider = document.querySelector(`[data-slider="${sliderId}"]`);
    // console.log('CommunityFit: Found slider element:', slider);

    if (!slider) {
      // console.error('CommunityFit: Could not find slider with data-slider:', sliderId);
      return;
    }

    const slides = slider.querySelectorAll('.community-fit-popup-slide');
    const dots = slider.querySelectorAll('.community-fit-popup-dot');

    // console.log('CommunityFit: Found slides:', slides.length, 'dots:', dots.length);

    // Update slides
    // console.log('CommunityFit: Updating slides to show slide index:', slideIndex);
    slides.forEach((slide, index) => {
      const wasActive = slide.classList.contains('active');

      // Use explicit add/remove instead of toggle
      if (index === slideIndex) {
        slide.classList.add('active');
      } else {
        slide.classList.remove('active');
      }

      const isActive = slide.classList.contains('active');
      // console.log(`CommunityFit: Slide ${index}: was ${wasActive ? 'active' : 'inactive'}, now ${isActive ? 'active' : 'inactive'}`);
    });

    // Update dots
    // console.log('CommunityFit: Updating dots to show slide index:', slideIndex);
    dots.forEach((dot, index) => {
      if (index === slideIndex) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });

    // Handle videos in slider
    const videos = slider.querySelectorAll('.community-fit-popup-video');
    videos.forEach((video, index) => {
      const videoId = video.getAttribute('data-popup-video');
      const progressBar = slider.querySelector(`[data-progress="${videoId}"]`);

      if (index === slideIndex) {
        // Play video on active slide
        setTimeout(() => {
          video.play().catch(err => {
            console.log('CommunityFit: Auto-play prevented on slide change:', err);
          });
        }, 100);
      } else {
        // Pause and reset videos on inactive slides
        video.pause();
        video.currentTime = 0;
        if (progressBar) {
          progressBar.style.width = '0%';
        }
      }
    });

    // console.log('CommunityFit: Moved to slide:', slideIndex);
  }

  directSlideChange(slider, slideIndex) {
    // console.log('CommunityFit: directSlideChange called with slideIndex:', slideIndex);

    const slides = slider.querySelectorAll('.community-fit-popup-slide');
    const dots = slider.querySelectorAll('.community-fit-popup-dot');

        // console.log('CommunityFit: Direct slide change - found slides:', slides.length, 'dots:', dots.length);

                // Update slides
    slides.forEach((slide, index) => {
      // Use explicit add/remove instead of toggle
      if (index === slideIndex) {
        slide.classList.add('active');
      } else {
        slide.classList.remove('active');
      }
    });

    // Update dots
    // console.log('CommunityFit: Direct updating dots to show slide index:', slideIndex);
    dots.forEach((dot, index) => {
      if (index === slideIndex) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });

    // Handle videos in slider (same logic as goToSlide)
    const videos = slider.querySelectorAll('.community-fit-popup-video');
    videos.forEach((video, index) => {
      const videoId = video.getAttribute('data-popup-video');
      const progressBar = slider.querySelector(`[data-progress="${videoId}"]`);

      if (index === slideIndex) {
        // Play video on active slide
        setTimeout(() => {
          video.play().catch(err => {
            console.log('CommunityFit: Auto-play prevented on slide change:', err);
          });
        }, 100);
      } else {
        // Pause and reset videos on inactive slides
        video.pause();
        video.currentTime = 0;
        if (progressBar) {
          progressBar.style.width = '0%';
        }
      }
    });

    // console.log('CommunityFit: Direct moved to slide:', slideIndex);
  }

  nextSlide() {
    // console.log('CommunityFit: nextSlide called, currentSlider:', this.currentSlider);
    if (!this.currentSlider) {
      // console.log('CommunityFit: No currentSlider set!');
      return;
    }

    const slides = this.currentSlider.querySelectorAll('.community-fit-popup-slide');
    const activeSlide = this.currentSlider.querySelector('.community-fit-popup-slide.active');
    const currentIndex = Array.from(slides).indexOf(activeSlide);
    const nextIndex = (currentIndex + 1) % slides.length;
    const sliderId = this.currentSlider.getAttribute('data-slider');

    // console.log('CommunityFit: Moving from slide', currentIndex, 'to', nextIndex);
    this.goToSlide(sliderId, nextIndex);
  }

  previousSlide() {
    // console.log('CommunityFit: previousSlide called, currentSlider:', this.currentSlider);
    if (!this.currentSlider) {
      // console.log('CommunityFit: No currentSlider set!');
      return;
    }

    const slides = this.currentSlider.querySelectorAll('.community-fit-popup-slide');
    const activeSlide = this.currentSlider.querySelector('.community-fit-popup-slide.active');
    const currentIndex = Array.from(slides).indexOf(activeSlide);
    const prevIndex = currentIndex === 0 ? slides.length - 1 : currentIndex - 1;
    const sliderId = this.currentSlider.getAttribute('data-slider');

    // console.log('CommunityFit: Moving from slide', currentIndex, 'to', prevIndex);
    this.goToSlide(sliderId, prevIndex);
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swiped left - go to next slide
        this.nextSlide();
      } else {
        // Swiped right - go to previous slide
        this.previousSlide();
      }
    }
  }

    toggleWishlist(productId, button) {
    // console.log('CommunityFit: Toggling wishlist for product:', productId);

    button.classList.toggle('active');

    if (button.classList.contains('active')) {
      button.setAttribute('aria-label', 'Remove from wishlist');
      // console.log('CommunityFit: Added to wishlist:', productId);
    } else {
      button.setAttribute('aria-label', 'Add to wishlist');
      // console.log('CommunityFit: Removed from wishlist:', productId);
    }
  }

    disableScroll() {
    // Store current scroll position
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Apply comprehensive scroll prevention
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';
    document.body.classList.add('community-fit-popup-open');

    // Bind event handlers to maintain context
    this.boundPreventScroll = this.preventScroll.bind(this);
    this.boundPreventScrollKeys = this.preventScrollKeys.bind(this);

    // Prevent wheel events
    document.addEventListener('wheel', this.boundPreventScroll, { passive: false });
    document.addEventListener('touchmove', this.boundPreventScroll, { passive: false });
    document.addEventListener('keydown', this.boundPreventScrollKeys, { passive: false });

    console.log('CommunityFit: Scroll disabled, position stored:', this.scrollPosition);
  }

  enableScroll() {
    // Remove scroll prevention
    if (this.boundPreventScroll) {
      document.removeEventListener('wheel', this.boundPreventScroll);
      document.removeEventListener('touchmove', this.boundPreventScroll);
    }
    if (this.boundPreventScrollKeys) {
      document.removeEventListener('keydown', this.boundPreventScrollKeys);
    }

    // Restore body styles
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.classList.remove('community-fit-popup-open');

    // Restore scroll position
    window.scrollTo(0, this.scrollPosition);

    console.log('CommunityFit: Scroll enabled, position restored:', this.scrollPosition);
  }

  preventScroll(e) {
    // Allow scrolling within popup content
    const popupContent = e.target.closest('.community-fit-popup-content');
    if (popupContent) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    return false;
  }

    preventScrollKeys(e) {
    // Allow scrolling within popup content
    const popupContent = e.target.closest('.community-fit-popup-content');
    if (popupContent) {
      return;
    }

    // Prevent scroll keys (space, page up/down, arrow keys, home/end)
    const scrollKeys = [32, 33, 34, 35, 36, 37, 38, 39, 40];
    if (scrollKeys.includes(e.keyCode)) {
      e.preventDefault();
      return false;
    }
  }

  initDragScrolling() {
    // Initialize drag scrolling for product sliders
    const sliders = document.querySelectorAll('.community-fit-popup-products-list.slider');

    sliders.forEach(slider => {
      this.setupDragScrolling(slider);
    });

    console.log('CommunityFit: Drag scrolling initialized for', sliders.length, 'sliders');
  }

  setupDragScrolling(slider) {
    let isDown = false;
    let startX;
    let scrollLeft;
    let isDragging = false;

    const mouseDownHandler = (e) => {
      // Only enable drag on desktop (not on mobile/touch devices)
      if (e.pointerType === 'touch') return;

      isDown = true;
      isDragging = false;
      slider.classList.add('dragging');
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
      e.preventDefault(); // Prevent text selection
    };

        const mouseUpHandler = () => {
      isDown = false;

      // Only delay if we were actually dragging
      if (isDragging) {
        // Delay removing dragging class to prevent immediate clicks after drag
        setTimeout(() => {
          slider.classList.remove('dragging');
          isDragging = false;
        }, 100);
      } else {
        // Not dragging, remove immediately to allow normal clicks
        slider.classList.remove('dragging');
        isDragging = false;
      }
    };

        const mouseMoveHandler = (e) => {
      if (!isDown) return;
      e.preventDefault();

      const x = e.pageX - slider.offsetLeft;
      const dragDistance = Math.abs(x - startX);

      // Mark as dragging if moved more than 5px (increased threshold)
      if (dragDistance > 5 && !isDragging) {
        isDragging = true;
      }

      // Only scroll if we've determined this is a drag
      if (isDragging) {
        const walk = (x - startX) * 1; // Reduced scroll speed multiplier
        slider.scrollLeft = scrollLeft - walk;
      }
    };

         // Enhanced click prevention - only prevent if actually dragged
     const clickHandler = (e) => {
       if (isDragging) {
         e.preventDefault();
         e.stopPropagation();
         e.stopImmediatePropagation();
         return false;
       }
     };

    // Add event listeners
    slider.addEventListener('mousedown', mouseDownHandler);
    slider.addEventListener('mouseleave', mouseUpHandler);
    slider.addEventListener('mouseup', mouseUpHandler);
    slider.addEventListener('mousemove', mouseMoveHandler);

    // Add click prevention with capture to catch all child clicks
    slider.addEventListener('click', clickHandler, true);

         // Also prevent clicks on all child elements during/after drag
     const preventChildClicks = (e) => {
       if (isDragging) {
         e.preventDefault();
         e.stopPropagation();
         e.stopImmediatePropagation();
         return false;
       }
     };

         // Find all clickable elements and add prevention
     const clickableElements = slider.querySelectorAll('a, button, [data-product-id]');
     clickableElements.forEach(element => {
       element.addEventListener('click', preventChildClicks, true);
     });

     // Store handlers for cleanup
     slider._productScrollHandlers = {
       mouseDownHandler,
       mouseUpHandler,
       mouseMoveHandler,
       clickHandler,
       preventChildClicks,
       clickableElements
     };
   }

    setupDragSliding(slider) {
    let isDown = false;
    let startX;
    let currentX;
    let isDragging = false;
    const sliderId = slider.getAttribute('data-slider');
    const slides = slider.querySelectorAll('.community-fit-popup-slide');
    const slideThreshold = 30; // Minimum drag distance to trigger slide change

    // console.log('CommunityFit: Setup drag for slider element:', slider);
    // console.log('CommunityFit: Slider ID from data-slider:', sliderId);

    if (slides.length <= 1) {
      // console.log('CommunityFit: No drag sliding needed - only one slide');
      return; // No need for drag sliding with single slide
    }

    const slidesContainer = slider.querySelector('.community-fit-popup-slides');

    if (!slidesContainer) {
      // console.error('CommunityFit: Could not find .community-fit-popup-slides element in slider:', sliderId);
      return;
    }

    // console.log('CommunityFit: Setting up drag sliding for', slides.length, 'slides');
    // console.log('CommunityFit: Slides container found:', slidesContainer);

    // Arrow functions to maintain correct 'this' context
    const mouseDownHandler = (e) => {
      // console.log('CommunityFit: Mouse down event detected on slider:', sliderId, 'target:', e.target);

      // Only enable drag on desktop (not on mobile/touch devices)
      if (e.pointerType === 'touch') {
        // console.log('CommunityFit: Ignoring touch pointer type');
        return;
      }

      // console.log('CommunityFit: Mouse down on slider - starting drag');
      isDown = true;
      isDragging = false;
      startX = e.pageX;
      currentX = startX;
      slidesContainer.style.cursor = 'grabbing';
      // Don't prevent default here to allow vertical scrolling to work
    };

    const mouseUpHandler = (e) => {
      if (!isDown) {
        console.log('CommunityFit: Mouse up but not dragging for slider:', sliderId);
        return;
      }

      console.log('CommunityFit: Mouse up - isDragging:', isDragging, 'for slider:', sliderId);
      isDown = false;

      if (isDragging) {
        const dragDistance = currentX - startX;
        console.log('CommunityFit: Drag distance:', dragDistance, 'Threshold:', slideThreshold);

                if (Math.abs(dragDistance) > slideThreshold) {
          const slides = slider.querySelectorAll('.community-fit-popup-slide');
          const activeSlide = slider.querySelector('.community-fit-popup-slide.active');
          const currentIndex = Array.from(slides).indexOf(activeSlide);

          if (dragDistance > 0) {
            // Dragged right - go to previous slide
            console.log('CommunityFit: Dragging right - previous slide');
            const prevIndex = currentIndex === 0 ? slides.length - 1 : currentIndex - 1;

            // Direct slide change without relying on sliderId lookup
            if (sliderId) {
              this.goToSlide(sliderId, prevIndex);
            } else {
              console.log('CommunityFit: Using direct slide change (no sliderId)');
              this.directSlideChange(slider, prevIndex);
            }
          } else {
            // Dragged left - go to next slide
            console.log('CommunityFit: Dragging left - next slide');
            const nextIndex = (currentIndex + 1) % slides.length;

            // Direct slide change without relying on sliderId lookup
            if (sliderId) {
              this.goToSlide(sliderId, nextIndex);
            } else {
              console.log('CommunityFit: Using direct slide change (no sliderId)');
              this.directSlideChange(slider, nextIndex);
            }
          }
        } else {
          console.log('CommunityFit: Drag distance too small - no slide change');
        }
      }

      isDragging = false;
      if (slidesContainer) {
        slidesContainer.style.cursor = 'grab';
      }
    };

    const mouseMoveHandler = (e) => {
      if (!isDown) return;

      currentX = e.pageX;
      const dragDistance = Math.abs(currentX - startX);

      // Mark as dragging if moved more than 5px
      if (dragDistance > 5 && !isDragging) {
        isDragging = true;
        // console.log('CommunityFit: Started dragging for slider:', sliderId, 'distance:', dragDistance);
        // Only prevent default when we've determined this is a horizontal drag
        e.preventDefault();
      }

      // Only prevent default if we're actively dragging horizontally
      if (isDragging) {
        e.preventDefault();
      }
    };

    const clickHandler = (e) => {
      if (isDragging) {
        console.log('CommunityFit: Preventing click during drag');
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Touch event handlers for mobile
    const touchStartHandler = (e) => {
      // console.log('CommunityFit: Touch start on slider');
      isDown = true;
      isDragging = false;
      startX = e.touches[0].pageX;
      currentX = startX;
      // Don't prevent default here to allow vertical scrolling
    };

    const touchEndHandler = (e) => {
      if (!isDown) return;

      // console.log('CommunityFit: Touch end - isDragging:', isDragging);
      isDown = false;

      if (isDragging) {
        const dragDistance = currentX - startX;
        // console.log('CommunityFit: Touch drag distance:', dragDistance, 'Threshold:', slideThreshold);

                if (Math.abs(dragDistance) > slideThreshold) {
          const slides = slider.querySelectorAll('.community-fit-popup-slide');
          const activeSlide = slider.querySelector('.community-fit-popup-slide.active');
          const currentIndex = Array.from(slides).indexOf(activeSlide);

          if (dragDistance > 0) {
            // Dragged right - go to previous slide
            // console.log('CommunityFit: Touch dragging right - previous slide');
            const prevIndex = currentIndex === 0 ? slides.length - 1 : currentIndex - 1;

            // Direct slide change without relying on sliderId lookup
            if (sliderId) {
              this.goToSlide(sliderId, prevIndex);
            } else {
              // console.log('CommunityFit: Using direct touch slide change (no sliderId)');
              this.directSlideChange(slider, prevIndex);
            }
          } else {
            // Dragged left - go to next slide
            // console.log('CommunityFit: Touch dragging left - next slide');
            const nextIndex = (currentIndex + 1) % slides.length;

            // Direct slide change without relying on sliderId lookup
            if (sliderId) {
              this.goToSlide(sliderId, nextIndex);
            } else {
              // console.log('CommunityFit: Using direct touch slide change (no sliderId)');
              this.directSlideChange(slider, nextIndex);
            }
          }
        } else {
          // console.log('CommunityFit: Touch drag distance too small - no slide change');
        }
      }

      isDragging = false;
    };

    const touchMoveHandler = (e) => {
      if (!isDown) return;

      currentX = e.touches[0].pageX;
      const dragDistance = Math.abs(currentX - startX);

      // Mark as dragging if moved more than 5px
      if (dragDistance > 5 && !isDragging) {
        isDragging = true;
        // console.log('CommunityFit: Started touch dragging');
        // Only prevent default when we've determined this is a horizontal drag
        e.preventDefault();
      }

      // Only prevent default if we're actively dragging horizontally
      if (isDragging) {
        e.preventDefault();
      }
    };

    // Update event handlers object to include touch events
    const eventHandlers = {
      mouseDownHandler,
      mouseUpHandler,
      mouseMoveHandler,
      clickHandler,
      touchStartHandler,
      touchEndHandler,
      touchMoveHandler
    };

    // Store handlers on the slider element for cleanup
    slidesContainer._dragHandlers = eventHandlers;

    // Add mouse event listeners (desktop)
    slidesContainer.addEventListener('mousedown', mouseDownHandler);
    document.addEventListener('mouseup', mouseUpHandler);
    document.addEventListener('mousemove', mouseMoveHandler);
    slidesContainer.addEventListener('click', clickHandler);

    // Add touch event listeners (mobile)
    slidesContainer.addEventListener('touchstart', touchStartHandler, { passive: false });
    slidesContainer.addEventListener('touchend', touchEndHandler, { passive: true });
    slidesContainer.addEventListener('touchmove', touchMoveHandler, { passive: false });

    // Add visual feedback
    slidesContainer.style.cursor = 'grab';
  }


  initializePopupVideos(popup) {
    const videos = popup.querySelectorAll('.community-fit-popup-video');
    console.log('CommunityFit: Initializing popup videos:', videos.length);

    videos.forEach(video => {
      const videoId = video.getAttribute('data-popup-video');
      const progressBar = popup.querySelector(`[data-progress="${videoId}"]`);

      // Auto-play the first visible video (single video or first in slider)
      const isFirstSlide = video.closest('.community-fit-popup-slide')?.classList.contains('active') !== false;
      const isSingleVideo = video.getAttribute('data-video-single') === 'true';

      if (isSingleVideo || isFirstSlide) {
        setTimeout(() => {
          video.play().catch(err => {
            console.log('CommunityFit: Auto-play prevented:', err);
          });
        }, 300); // Small delay to ensure popup is fully open
      }

      // Click to play/pause
      video.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      });

      // Update progress bar
      video.addEventListener('timeupdate', () => {
        if (progressBar && video.duration) {
          const progress = (video.currentTime / video.duration) * 100;
          progressBar.style.width = `${progress}%`;
        }
      });

      // Reset progress when video ends
      video.addEventListener('ended', () => {
        if (progressBar) {
          progressBar.style.width = '0%';
        }
      });

      // Reset progress when video is paused at beginning
      video.addEventListener('pause', () => {
        if (video.currentTime === 0 && progressBar) {
          progressBar.style.width = '0%';
        }
      });

      console.log('CommunityFit: Popup video initialized:', videoId);
    });
  }

  initializeNotifyButton(popup) {
    const button = popup.querySelector('[data-notify-button]');
    if (!button) return;

    const textEl = button.querySelector('.community-fit-popup-notify-text');
    const iconEl = button.querySelector('.community-fit-popup-notify-icon');
    const defaultText = textEl.dataset.notifyText;
    const notifiedText = textEl.dataset.notifiedText;

    let notifyTimeout = null;
    let revertTimeout = null;

    const showNotified = () => {
      textEl.textContent = notifiedText;
      iconEl.classList.remove('hidden');
      button.classList.add('community-fit-popup-notify-button--notified');

      clearTimeout(revertTimeout);
      revertTimeout = setTimeout(() => {
        textEl.textContent = defaultText;
        iconEl.classList.add('hidden');
        button.classList.remove('community-fit-popup-notify-button--notified');
      }, 15000);
    };

    const handleClick = () => {
      const dropDate = button.getAttribute('data-drop-date');
      const dropTitle = button.getAttribute('data-drop-title') || 'Drop';
      if (!dropDate) return;

      const icsBlob = this.buildDropDateICS(dropDate, dropTitle);
      if (!icsBlob) {
        console.error('CommunityFit: Invalid drop date, cannot generate calendar event:', dropDate);
        return;
      }

      const icsUrl = URL.createObjectURL(icsBlob);
      const tempLink = document.createElement('a');
      tempLink.href = icsUrl;
      tempLink.download = 'event.ics';
      document.body.appendChild(tempLink);
      tempLink.click();
      tempLink.remove();
      setTimeout(() => URL.revokeObjectURL(icsUrl), 1000);

      clearTimeout(notifyTimeout);
      notifyTimeout = setTimeout(showNotified, 1000);
    };

    button.addEventListener('click', handleClick);

    popup._notifyCleanup = () => {
      clearTimeout(notifyTimeout);
      clearTimeout(revertTimeout);
      button.removeEventListener('click', handleClick);
    };
  }

  buildDropDateICS(dropDate, title) {
    const startDate = new Date(`${dropDate}T00:00:00`);
    if (isNaN(startDate.getTime())) return null;

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 1);

    const toICSDate = (date) => date.toISOString().slice(0, 10).replace(/-/g, '');
    const escapeICS = (value) => String(value).replace(/([,;])/g, '\\$1');

    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//LFDY//Community Fit//EN',
      'BEGIN:VEVENT',
      `UID:${toICSDate(startDate)}-${Math.random().toString(36).slice(2)}@lfdy`,
      `DTSTART;VALUE=DATE:${toICSDate(startDate)}`,
      `DTEND;VALUE=DATE:${toICSDate(endDate)}`,
      `SUMMARY:${escapeICS(title)}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');

    return new Blob([lines], { type: 'text/calendar' });
  }

  checkLazyLoading() {
    const images = document.querySelectorAll('.community-fit-image[data-index]');
    console.log('CommunityFit: Found images with lazy loading:', images.length);

    images.forEach(img => {
      const index = parseInt(img.getAttribute('data-index'));
      const isLazy = img.getAttribute('data-lazy') === 'true';
      const loadingAttr = img.getAttribute('loading');

      console.log(`CommunityFit: Image ${index} - Lazy: ${isLazy}, Loading attr: ${loadingAttr}`);

      // Check if browser supports native lazy loading
      if ('loading' in HTMLImageElement.prototype) {
        console.log('CommunityFit: Browser supports native lazy loading');
      } else {
        console.log('CommunityFit: Browser does not support native lazy loading - implementing fallback');
        this.implementLazyLoadingFallback(images);
      }
    });
  }

  implementLazyLoadingFallback(images) {
    if (!('IntersectionObserver' in window)) {
      console.log('CommunityFit: No IntersectionObserver support - loading all images');
      return;
    }

    const lazyImages = Array.from(images).filter(img =>
      img.getAttribute('data-lazy') === 'true'
    );

    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          console.log('CommunityFit: Lazy loading image:', img.getAttribute('data-index'));
          observer.unobserve(img);
        }
      });
    }, {
      rootMargin: '50px'
    });

    lazyImages.forEach(img => {
      imageObserver.observe(img);
    });

    console.log('CommunityFit: Lazy loading fallback implemented for', lazyImages.length, 'images');
  }

  setupIOSAutoplayWorkarounds() {
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (!isIOS) {
      return;
    }

    // Strategy 1: Try autoplay on any user interaction
    const tryAutoplayOnInteraction = () => {
      this.attemptAutoplayAllVideos();

      // Remove listeners after first successful interaction
      document.removeEventListener('touchstart', tryAutoplayOnInteraction);
      document.removeEventListener('click', tryAutoplayOnInteraction);
      document.removeEventListener('scroll', tryAutoplayOnInteraction);
    };

    // Add interaction listeners
    document.addEventListener('touchstart', tryAutoplayOnInteraction, { once: true });
    document.addEventListener('click', tryAutoplayOnInteraction, { once: true });
    document.addEventListener('scroll', tryAutoplayOnInteraction, { once: true });

    // Strategy 2: Multiple autoplay attempts with delays
    setTimeout(() => this.attemptAutoplayAllVideos(), 1000);
    setTimeout(() => this.attemptAutoplayAllVideos(), 3000);
    setTimeout(() => this.attemptAutoplayAllVideos(), 5000);

    // Strategy 3: Intersection observer with lower threshold
    this.setupAggressiveIntersectionObserver();

    // Strategy 4: Immediate check for videos already in viewport
    setTimeout(() => {
      this.checkAllVideosInViewport();
    }, 100);
  }

  checkAllVideosInViewport() {
    const videos = document.querySelectorAll('.community-fit-video');

    videos.forEach(video => {
      if (!video.paused) return; // Skip if already playing

      const rect = video.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const windowWidth = window.innerWidth || document.documentElement.clientWidth;

      // Check if video is in viewport
      const isInViewport = rect.top < windowHeight &&
                          rect.bottom > 0 &&
                          rect.left < windowWidth &&
                          rect.right > 0;

      if (isInViewport) {
        const videoId = video.getAttribute('data-video-id');

        // Reset flags and try autoplay
        video._autoplayAttempted = false;
        video._retryAttempted = false;

        this.attemptAutoplay(video, videoId);
      }
    });
  }

  async openProductWithStikky(productId) {
    // console.log('CommunityFit: Opening product with Stikky SDK:', productId);

    try {
      // Check if StikkySDK is available
      if (typeof StikkySDK === 'undefined') {
        // console.error('CommunityFit: StikkySDK not available');
        return;
      }

      const openProductResult = await StikkySDK.actions.openProduct({
        productId: productId
      });

      openProductResult.fold(
        error => console.error(`openProduct Failed: ${error.message} (Type: ${error.type}, Name: ${error.name})`),
        () => console.log("openProduct Success!")
      );
    } catch (error) {
      console.error('CommunityFit: Error opening product with Stikky SDK:', error);
    }
  }

  async openCollectionWithStikky(collectionId) {
    // console.log('CommunityFit: Opening collection with Stikky SDK:', collectionId);

    try {
      // Check if StikkySDK is available
      if (typeof StikkySDK === 'undefined') {
        // console.error('CommunityFit: StikkySDK not available');
        return;
      }

      const openCollectionResult = await StikkySDK.actions.openCollection({
        id: collectionId
      });

      openCollectionResult.fold(
        error => console.error(`openCollection Failed: ${error.message} (Type: ${error.type}, Name: ${error.name})`),
        () => console.log("openCollection Success!")
      );
    } catch (error) {
      console.error('CommunityFit: Error opening collection with Stikky SDK:', error);
    }
  }

  async openUrlWithStikky(url) {
    // console.log('CommunityFit: Opening URL with Stikky SDK:', url);

    try {
      // Check if StikkySDK is available
      if (typeof StikkySDK === 'undefined') {
        // console.error('CommunityFit: StikkySDK not available');
        return;
      }

      const openUrlResult = await StikkySDK.actions.openUrl({
        url: url
      });

      openUrlResult.fold(
        error => console.error(`openUrl Failed: ${error.message} (Type: ${error.type}, Name: ${error.name})`),
        () => console.log("openUrl Success!")
      );
    } catch (error) {
      console.error('CommunityFit: Error opening URL with Stikky SDK:', error);
    }
  }

      attemptAutoplayAllVideos() {
    const videos = document.querySelectorAll('.community-fit-video');

    videos.forEach(video => {
      const videoId = video.getAttribute('data-video-id');

      // Reset retry flags to allow new attempts
      video._autoplayAttempted = false;
      video._retryAttempted = false;
      video._isPlaying = false;

      // Skip if already playing successfully
      if (video._autoplaySucceeded && !video.paused) {
        return;
      }

      // Try autoplay
      this.attemptAutoplay(video, videoId);
    });
  }

    setupAggressiveIntersectionObserver() {
    const videos = document.querySelectorAll('.community-fit-video');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const video = entry.target;
          const videoId = video.getAttribute('data-video-id');

          // Reset autoplay attempt and try again
          video._autoplayAttempted = false;

          setTimeout(() => {
            this.attemptAutoplay(video, videoId);
          }, 100);
        }
      });
    }, {
      threshold: 0.1, // Lower threshold for earlier triggering
      rootMargin: '50px' // Start earlier
    });

    videos.forEach(video => {
      observer.observe(video);

      // Check if video is already in viewport on page load
      this.checkIfVideoInViewport(video, observer);
    });

    // Store for cleanup
    this._aggressiveObserver = observer;
  }

  checkIfVideoInViewport(video, observer) {
    // Use requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      const rect = video.getBoundingClientRect();
      const windowHeight = window.innerHeight || document.documentElement.clientHeight;
      const windowWidth = window.innerWidth || document.documentElement.clientWidth;

      // Check if video is in viewport (with some margin for early trigger)
      const isInViewport = rect.top < windowHeight + 50 &&
                          rect.bottom > -50 &&
                          rect.left < windowWidth + 50 &&
                          rect.right > -50;

            if (isInViewport) {
        const videoId = video.getAttribute('data-video-id');

        // Try autoplay immediately for already visible videos
        setTimeout(() => {
          this.attemptAutoplay(video, videoId);
        }, 200); // Small delay to ensure everything is ready
      }
    });
  }
}

// Add cleanup method to CommunityFit class for proper section reloading
CommunityFit.prototype.cleanup = function() {
  console.log('CommunityFit: Cleaning up instance');

  // Close any open popups (this will also cleanup drag listeners)
  if (this.currentPopup) {
    this.closePopup();
  }

  // Clean up any remaining drag event listeners from all sliders
  const allSliders = document.querySelectorAll('.community-fit-popup-slides');
  allSliders.forEach(slidesContainer => {
    if (slidesContainer._dragHandlers) {
      const handlers = slidesContainer._dragHandlers;

      // Remove global event listeners
      document.removeEventListener('mouseup', handlers.mouseUpHandler);
      document.removeEventListener('mousemove', handlers.mouseMoveHandler);

      // Remove local event listeners
      slidesContainer.removeEventListener('mousedown', handlers.mouseDownHandler);
      slidesContainer.removeEventListener('click', handlers.clickHandler);

      // Remove touch event listeners
      if (handlers.touchStartHandler) {
        slidesContainer.removeEventListener('touchstart', handlers.touchStartHandler);
        slidesContainer.removeEventListener('touchend', handlers.touchEndHandler);
        slidesContainer.removeEventListener('touchmove', handlers.touchMoveHandler);
      }

            // Clear stored handlers
      delete slidesContainer._dragHandlers;
    }
  });

  // Clean up any remaining product slider drag event listeners
  const allProductSliders = document.querySelectorAll('.community-fit-popup-products-list.slider');
  allProductSliders.forEach(slider => {
    if (slider._productScrollHandlers) {
      const handlers = slider._productScrollHandlers;

      // Remove event listeners
      slider.removeEventListener('mousedown', handlers.mouseDownHandler);
      slider.removeEventListener('mouseleave', handlers.mouseUpHandler);
      slider.removeEventListener('mouseup', handlers.mouseUpHandler);
      slider.removeEventListener('mousemove', handlers.mouseMoveHandler);
      slider.removeEventListener('click', handlers.clickHandler, true);

      // Remove child element listeners
      if (handlers.clickableElements) {
        handlers.clickableElements.forEach(element => {
          element.removeEventListener('click', handlers.preventChildClicks, true);
        });
      }

      // Clear stored handlers
      delete slider._productScrollHandlers;
    }
  });

    // Clean up video observers and handlers
  const allVideos = document.querySelectorAll('.community-fit-video');
  allVideos.forEach(video => {
    // Clean up intersection observer
    if (video._intersectionObserver) {
      video._intersectionObserver.disconnect();
      video._intersectionObserver = null;
    }

    // Clean up manual play handler
    if (video._manualPlayHandler) {
      video.removeEventListener('click', video._manualPlayHandler);
      video._manualPlayHandler = null;
    }

    // Reset autoplay attempt flags
    video._autoplayAttempted = false;
    video._isPlaying = false;
    video._retryAttempted = false;
  });

  // Clean up aggressive observer for iOS
  if (this._aggressiveObserver) {
    this._aggressiveObserver.disconnect();
    this._aggressiveObserver = null;
  }

  // Destroy masonry instance if it exists
  if (this.masonry) {
    this.masonry.destroy();
    this.masonry = null;
  }

  // Clear the singleton instance
  CommunityFit.instance = null;
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CommunityFit();
  });
} else {
  new CommunityFit();
}

// Handle Shopify section events (for theme editor)
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('.community-fit')) {
    console.log('CommunityFit: Section loaded, creating new instance');
    new CommunityFit();
  }
});

document.addEventListener('shopify:section:unload', (e) => {
  if (e.target.querySelector('.community-fit') && CommunityFit.instance) {
    console.log('CommunityFit: Section unloaded, cleaning up');
    CommunityFit.instance.cleanup();
  }
});
